/**
 * DocsPage — loads markdown for the :slug param and renders it.
 *
 * Each markdown file is imported as a raw string via Vite's `?raw`
 * suffix, so all content ships in the bundle (no extra fetch). The
 * import map is keyed by slug.
 *
 * Prev/next links at the bottom; breadcrumb back to /docs at the top.
 */

import { Box, Stack, Typography } from '@mui/material';
import type { ReactElement } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { DocsMarkdown } from '@/pages/docs/DocsMarkdown';
import { DOCS, docIndex, neighbouring } from '@/pages/docs/nav';

import architectureMd from '@/pages/docs/content/architecture.md?raw';
import agenticLoopMd from '@/pages/docs/content/agentic-loop.md?raw';
import sentryRcaMd from '@/pages/docs/content/sentry-rca.md?raw';
import memoryMd from '@/pages/docs/content/memory.md?raw';
import mcpServerMd from '@/pages/docs/content/mcp-server.md?raw';
import llmBackendsMd from '@/pages/docs/content/llm-backends.md?raw';
import authSecurityMd from '@/pages/docs/content/auth-security.md?raw';
import asyncSchedulingMd from '@/pages/docs/content/async-scheduling.md?raw';
import integrationsMd from '@/pages/docs/content/integrations.md?raw';
import frontendMd from '@/pages/docs/content/frontend.md?raw';
import infrastructureMd from '@/pages/docs/content/infrastructure.md?raw';
import agentTeamsMd from '@/pages/docs/content/agent-teams.md?raw';

const CONTENT: Record<string, string> = {
  architecture: architectureMd,
  'agentic-loop': agenticLoopMd,
  'sentry-rca': sentryRcaMd,
  memory: memoryMd,
  'mcp-server': mcpServerMd,
  'llm-backends': llmBackendsMd,
  'auth-security': authSecurityMd,
  'async-scheduling': asyncSchedulingMd,
  integrations: integrationsMd,
  frontend: frontendMd,
  infrastructure: infrastructureMd,
  'agent-teams': agentTeamsMd,
};

export function DocsPage(): ReactElement {
  const { slug } = useParams<{ slug: string }>();
  const text = (slug && CONTENT[slug]) ?? '';
  const i = slug ? docIndex(slug) : -1;
  const doc = i !== -1 ? DOCS[i] : null;
  const { prev, next } = slug ? neighbouring(slug) : { prev: null, next: null };

  if (!doc || !text) {
    return (
      <Box>
        <Typography variant="h1">Not found</Typography>
        <Typography variant="body1" sx={{ mt: 1.5, color: 'text.secondary' }}>
          The doc page <code>{slug}</code> does not exist.{' '}
          <RouterLink to="/docs" style={{ color: 'inherit' }}>
            Back to docs index
          </RouterLink>
          .
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography
        variant="caption"
        component="div"
        sx={{ color: 'text.faintest', mb: 1.5 }}
      >
        <RouterLink to="/docs" style={{ color: 'inherit', textDecoration: 'none' }}>
          docs
        </RouterLink>
        <Box component="span" sx={{ mx: 0.75, opacity: 0.5 }}>
          /
        </Box>
        <Box component="span" sx={{ color: 'primary.main' }}>
          {doc.title.toLowerCase()}
        </Box>
      </Typography>

      <DocsMarkdown text={text} />

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          mt: 6,
          pt: 3,
          borderTop: '1px dashed',
          borderColor: 'divider',
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: 12,
        }}
      >
        {prev ? (
          <Box
            component={RouterLink}
            to={prev.slug === 'index' ? '/docs' : `/docs/${prev.slug}`}
            sx={{
              color: 'text.disabled',
              textDecoration: 'none',
              '&:hover': { color: 'primary.main' },
            }}
          >
            ← {prev.title.toLowerCase()}
          </Box>
        ) : (
          <Box />
        )}
        {next ? (
          <Box
            component={RouterLink}
            to={next.slug === 'index' ? '/docs' : `/docs/${next.slug}`}
            sx={{
              color: 'text.disabled',
              textDecoration: 'none',
              textAlign: 'right',
              '&:hover': { color: 'primary.main' },
            }}
          >
            {next.title.toLowerCase()} →
          </Box>
        ) : (
          <Box />
        )}
      </Stack>
    </Box>
  );
}
